import React, { useState } from 'react';
import { useWeb } from '../../context/WebContext';
import { FaTrashAlt, FaCheck, FaPhoneAlt, FaEnvelope } from 'react-icons/fa';
import toast from 'react-hot-toast';

export default function ManageEnquiries() {
  const { enquiries, markEnquiryRead, deleteEnquiry } = useWeb();
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const handleMarkRead = (id) => {
    markEnquiryRead(id);
    toast.success('Enquiry marked as read');
  };

  const handleDelete = (id) => {
    setConfirmDeleteId(id);
  };

  const confirmDelete = () => {
    deleteEnquiry(confirmDeleteId);
    toast.success('Enquiry deleted');
    setConfirmDeleteId(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-extrabold text-slate-900">Manage Customer Enquiries</h1>
        <p className="text-slate-500 text-xs sm:text-sm mt-1">Review and process inbound customer leads and service booking requests.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          {enquiries.length > 0 ? (
            <table className="w-full text-left text-xs sm:text-sm">
              <thead className="bg-slate-50 text-slate-500 font-bold uppercase text-[10px] tracking-wider border-b">
                <tr>
                  <th className="px-6 py-4">Customer Details</th>
                  <th className="px-6 py-4">Requested Service</th>
                  <th className="px-6 py-4">City</th>
                  <th className="px-6 py-4">Date & Time</th>
                  <th className="px-6 py-4">Message / Requirements</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-150">
                {enquiries.map((enq) => (
                  <tr key={enq.id} className={`hover:bg-slate-50/30 ${enq.status === 'unread' ? 'bg-orange-50/20 font-medium' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="text-slate-900 font-bold">{enq.name}</div>
                      <div className="flex items-center space-x-1.5 text-slate-500 text-xs mt-0.5">
                        <FaPhoneAlt className="text-[10px] text-slate-400" />
                        <a href={`tel:${enq.phone}`} className="hover:underline">{enq.phone}</a>
                      </div>
                      {enq.email && (
                        <div className="flex items-center space-x-1.5 text-slate-500 text-[10px]">
                          <FaEnvelope className="text-[8px] text-slate-400" />
                          <a href={`mailto:${enq.email}`} className="hover:underline">{enq.email}</a>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-slate-950 font-semibold">{enq.service}</td>
                    <td className="px-6 py-4 text-slate-600">{enq.location}</td>
                    <td className="px-6 py-4 text-slate-400 text-xs">
                      {new Date(enq.date).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-6 py-4 text-slate-600 max-w-xs break-words text-xs">
                      {enq.message || <span className="text-slate-400 italic">No message provided</span>}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-extrabold ${
                        enq.status === 'unread' 
                          ? 'bg-orange-100 text-orange-600' 
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {enq.status.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right space-x-2">
                      {enq.status === 'unread' && (
                        <button 
                          onClick={() => handleMarkRead(enq.id)}
                          className="text-green-600 hover:text-green-800 p-1.5 bg-green-50 hover:bg-green-100 rounded-lg transition"
                          title="Mark as Read"
                        >
                          <FaCheck className="text-xs" />
                        </button>
                      )}
                      <button 
                        onClick={() => handleDelete(enq.id)}
                        className="text-red-500 hover:text-red-750 p-1.5 bg-red-50 hover:bg-red-100 rounded-lg transition"
                        title="Delete Enquiry"
                      >
                        <FaTrashAlt className="text-xs" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-20 text-slate-400 text-xs sm:text-sm">
              No customer lead enquiries received yet.
            </div>
          )}
        </div>
      </div>
      {/* Confirm Delete Dialog */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-slate-950/50 backdrop-blur-sm" onClick={() => setConfirmDeleteId(null)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 w-full max-w-sm text-center space-y-4">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <FaTrashAlt className="text-red-600 text-lg" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Delete Enquiry?</h3>
            <p className="text-slate-500 text-sm">This customer lead will be permanently deleted.</p>
            <div className="flex gap-3 pt-2">
              <button onClick={() => setConfirmDeleteId(null)} className="flex-1 border border-slate-200 rounded-xl py-2.5 font-semibold text-slate-700 hover:bg-slate-50 transition">Cancel</button>
              <button onClick={confirmDelete} className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2.5 rounded-xl transition">Yes, Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
